import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../config/firebaseConfig";
import { useAuthState } from "react-firebase-hooks/auth";
import { LoadingDots } from "../components";
import { useSelector } from "react-redux";

export const isUnverifiedEmailUser = (user) => {
  if (!user) return false;
  if (user.emailVerified) return false;
  const isPasswordProvider = user.providerData?.some(
    (provider) => provider.providerId === "password"
  );
  const isEmailOnly = Boolean(user.email && !user.phoneNumber);
  return (isPasswordProvider || isEmailOnly) && !user.emailVerified;
};

const UserAuthProtected = ({ children, authenticate = true }) => {
  const [userAuth, authLoading] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, loading: userDetailsLoading } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    if (!authLoading) {
      const activeUser = userAuth || auth?.currentUser;

      if (authenticate) {
        // Protected routes (Dashboard, etc.)
        if (!activeUser) {
          const returnTo = location.pathname + location.search;
          const search =
            returnTo && returnTo !== "/" && returnTo !== "/dashboard"
              ? `?returnTo=${encodeURIComponent(returnTo)}`
              : "";
          navigate(`/login${search}`, {
            state: { from: location },
            replace: true,
          });
        } else if (isUnverifiedEmailUser(activeUser)) {
          // Email/password user must verify email before accessing dashboard
          navigate("/register/email");
        }
      } else if (!authenticate && activeUser) {
        // Guest routes (/login, /register)
        if (isUnverifiedEmailUser(activeUser)) {
          // Do NOT redirect unverified email user to dashboard.
          // Keep them on email auth view so they can see the verification sent screen.
          if (!location.pathname.includes("/email")) {
            navigate("/register/email");
          }
          return;
        }

        if (userDetailsLoading) {
          // Waiting for user details
        } else {
          const searchParams = new URLSearchParams(location.search);
          const isAiLab = searchParams.get("source") === "ai-lab";
          const demoType = searchParams.get("type");
          const returnTo = searchParams.get("returnTo") || location.state?.from?.pathname;

          let targetDashboard = returnTo || "/dashboard";
          if (demoType) {
            targetDashboard = `/dashboard${location.search}`;
          } else if (isAiLab) {
            targetDashboard = "/dashboard?offer=ai-lab";
          }

          if (userDetails?.accountType === "school") {
            navigate("/schoolDashboard", { replace: true });
          } else {
            navigate(targetDashboard, { replace: true });
          }
        }
      }
    }
  }, [
    authenticate,
    navigate,
    authLoading,
    userAuth,
    userDetails,
    userDetailsLoading,
    location.search,
    location.pathname,
  ]);

  if (authLoading) return <LoadingDots />;
  if (authenticate) {
    const activeUser = userAuth || auth?.currentUser;
    if (!activeUser || isUnverifiedEmailUser(activeUser)) {
      return <LoadingDots />;
    }
    if (userDetailsLoading) {
      return <LoadingDots />;
    }
  }

  return children;
};

export default UserAuthProtected;
